import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ApiError, type components } from '@akillidernek/api-client'
import {
  Badge,
  Button,
  Card,
  CardBody,
  Dialog,
  EmptyState,
  Field,
  Icon,
  Input,
  useToast,
} from '@akillidernek/ui'
import { api } from '../api'
import { useTenantSlug } from '../use-tenant'
import { OzelAlanlar, ozelAlanDegerleri, useOzelAlanlar } from '../ozel-alanlar'
import { tl } from './aidat'
import { UyeAktarim } from './uye-aktarim'

type Member = components['schemas']['MemberRead']
type MembershipType = components['schemas']['MembershipTypeRead']
type Status = Member['status']

const DURUM: Record<
  Status,
  { label: string; tone: 'success' | 'warning' | 'danger' | 'info' | 'neutral' }
> = {
  pending: { label: 'Başvuru', tone: 'info' },
  active: { label: 'Aktif', tone: 'success' },
  passive: { label: 'Pasif', tone: 'warning' },
  resigned: { label: 'İstifa', tone: 'neutral' },
  expelled: { label: 'İhraç', tone: 'danger' },
  deceased: { label: 'Vefat', tone: 'neutral' },
  rejected: { label: 'Reddedildi', tone: 'danger' },
}

/** Sunucu sayfalaması: üye listesi binlerce satır olabilir (bkz. DataTable notu). */
const SAYFA = 25

/** Sipariş durumları (orders/state.py) — ekstrede insan okunur. */
const EKSTRE_DURUM: Record<string, string> = {
  draft: 'Taslak',
  awaiting_payment: 'Ödenmedi',
  paid: 'Ödendi',
  partially_refunded: 'Kısmen iade',
  refunded: 'İade edildi',
  cancelled: 'İptal',
}
/** Hâlâ tahsil edilmesi beklenen durumlar. İade/iptal borç değildir. */
const ACIK_BORC = new Set(['draft', 'awaiting_payment'])

function bugun(): string {
  return new Date().toISOString().slice(0, 10)
}

/**
 * Üye listesi — YES-105.
 *
 * Arama ve süzme SUNUCUDA: `DataTable`ın istemci tarafı araması yüzler
 * mertebesindeki listeler için; üye listesi binlere çıkıyor ve tamamını
 * indirip filtrelemek hem yavaş hem gereksiz veri taşıması olurdu.
 */
export function Uyeler() {
  const slug = useTenantSlug()
  const [arama, setArama] = useState('')
  const [durum, setDurum] = useState<Status | ''>('')
  const [sayfa, setSayfa] = useState(0)
  const [seciliUye, setSeciliUye] = useState<Member | null>(null)
  const [yeniAcik, setYeniAcik] = useState(false)
  /** Toplu işlem için işaretli üyeler (YES-160). Sayfa/süzgeç değişince korunur. */
  const [isaretli, setIsaretli] = useState<Set<string>>(new Set())

  const liste = useQuery({
    queryKey: ['members', slug, arama, durum, sayfa],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/members', {
        params: {
          query: {
            q: arama || undefined,
            membership_status: durum || undefined,
            limit: SAYFA,
            offset: sayfa * SAYFA,
          },
        },
      })
      return data
    },
  })

  const tipler = useQuery({
    queryKey: ['membership-types', slug],
    enabled: Boolean(slug),
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/members/types')
      return data ?? []
    },
  })

  const toplam = liste.data?.total ?? 0
  const uyeler = liste.data?.items ?? []
  const sonSayfa = Math.max(0, Math.ceil(toplam / SAYFA) - 1)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold">Üyeler</h1>
        {liste.data && (
          <span className="text-sm text-[var(--ad-text-muted)]">
            {toplam.toLocaleString('tr')} kayıt
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <UyeAktarim tipler={tipler.data ?? []} />
          <Button onClick={() => setYeniAcik(true)}>
            <Icon name="plus" size={16} aria-hidden />
            Üye ekle
          </Button>
        </div>
      </div>

      <Card>
        <CardBody className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-2">
            <Input
              className="max-w-xs"
              placeholder="Ad, e-posta, telefon, üye no…"
              value={arama}
              onChange={(event) => {
                setArama(event.target.value)
                // Süzgeç değişince ilk sayfaya dön: 5. sayfadayken arama
                // yapmak, çoğu zaman boş bir sayfa gösterirdi.
                setSayfa(0)
              }}
            />
            <select
              aria-label="Duruma göre süz"
              className="h-10 rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
              value={durum}
              onChange={(event) => {
                setDurum(event.target.value as Status | '')
                setSayfa(0)
              }}
            >
              <option value="">Tüm durumlar</option>
              {Object.entries(DURUM).map(([value, { label }]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {isaretli.size > 0 && (
            <TopluIslem
              kimlikler={[...isaretli]}
              tipler={tipler.data ?? []}
              slug={slug}
              onBitti={() => setIsaretli(new Set())}
            />
          )}

          {uyeler.length === 0 ? (
            <EmptyState
              icon={<Icon name="users" size={24} aria-hidden />}
              title={arama || durum ? 'Eşleşen üye yok' : 'Henüz üye yok'}
              description={
                arama || durum
                  ? 'Süzgeçleri değiştirip tekrar deneyin.'
                  : 'İlk üyeyi ekleyin ya da Excel’den aktarın.'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[var(--ad-border)] text-left text-xs text-[var(--ad-text-muted)]">
                    <th className="w-8 py-2 pr-2">
                      <input
                        type="checkbox"
                        aria-label="Sayfadaki tüm üyeleri seç"
                        checked={uyeler.length > 0 && uyeler.every((uye) => isaretli.has(uye.id))}
                        onChange={(event) =>
                          setIsaretli((onceki) => {
                            const sonraki = new Set(onceki)
                            uyeler.forEach((uye) =>
                              event.target.checked ? sonraki.add(uye.id) : sonraki.delete(uye.id),
                            )
                            return sonraki
                          })
                        }
                      />
                    </th>
                    <th className="py-2 pr-3 font-medium">Üye no</th>
                    <th className="py-2 pr-3 font-medium">Ad soyad</th>
                    <th className="py-2 pr-3 font-medium">Üyelik tipi</th>
                    <th className="py-2 pr-3 font-medium">Durum</th>
                    <th className="py-2 pr-3 font-medium">İletişim</th>
                  </tr>
                </thead>
                <tbody>
                  {uyeler.map((uye) => (
                    <tr
                      key={uye.id}
                      onClick={() => setSeciliUye(uye)}
                      className="cursor-pointer border-b border-[var(--ad-border)] last:border-0 hover:bg-[var(--ad-surface-muted)]"
                    >
                      {/* Satır tıklaması detayı açar; kutu tıklaması AÇMAMALI. */}
                      <td className="py-2 pr-2" onClick={(event) => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`${uye.first_name} ${uye.last_name} seç`}
                          checked={isaretli.has(uye.id)}
                          onChange={(event) =>
                            setIsaretli((onceki) => {
                              const sonraki = new Set(onceki)
                              if (event.target.checked) sonraki.add(uye.id)
                              else sonraki.delete(uye.id)
                              return sonraki
                            })
                          }
                        />
                      </td>
                      <td className="py-2 pr-3 tabular-nums text-[var(--ad-text-muted)]">
                        {uye.member_no ?? '—'}
                      </td>
                      <td className="py-2 pr-3 font-medium">
                        {uye.first_name} {uye.last_name}
                      </td>
                      <td className="py-2 pr-3">{uye.membership_type_name}</td>
                      <td className="py-2 pr-3">
                        <Badge tone={DURUM[uye.status].tone}>{DURUM[uye.status].label}</Badge>
                      </td>
                      <td className="py-2 pr-3 text-[var(--ad-text-muted)]">
                        {uye.email ?? uye.phone ?? '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {toplam > SAYFA && (
            <div className="flex items-center gap-2 text-sm">
              <Button
                variant="secondary"
                size="sm"
                disabled={sayfa === 0}
                onClick={() => setSayfa((n) => n - 1)}
              >
                Önceki
              </Button>
              <span className="text-[var(--ad-text-muted)]">
                {sayfa + 1} / {sonSayfa + 1}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={sayfa >= sonSayfa}
                onClick={() => setSayfa((n) => n + 1)}
              >
                Sonraki
              </Button>
            </div>
          )}
        </CardBody>
      </Card>

      <YeniUye acik={yeniAcik} onAcik={setYeniAcik} tipler={tipler.data ?? []} slug={slug} />
      {seciliUye && <UyeDetay uye={seciliUye} onKapat={() => setSeciliUye(null)} slug={slug} />}
    </div>
  )
}

/**
 * Toplu işlem çubuğu — YES-160. Kısmi başarı normal: yirmi üyeden ikisinin
 * durumu geçişe izin vermez; sunucu satır satır raporlar, biz sayarız.
 */
function TopluIslem({
  kimlikler,
  tipler,
  slug,
  onBitti,
}: {
  kimlikler: string[]
  tipler: MembershipType[]
  slug: string | null
  onBitti: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hedefDurum, setHedefDurum] = useState<Status | ''>('')
  const [hedefTip, setHedefTip] = useState('')

  const sonucuBildir = (sonuc: { updated: number; errors: { detail: string }[] } | undefined) => {
    if (!sonuc) return
    void queryClient.invalidateQueries({ queryKey: ['members', slug] })
    if (sonuc.errors.length === 0) {
      toast({ title: `${sonuc.updated} üye güncellendi`, tone: 'success' })
    } else {
      // İlk hatanın metni yeter: hepsi aynı sebeptir çoğu zaman
      // ("pending durumundan passive'e geçemez").
      toast({
        title: `${sonuc.updated} güncellendi, ${sonuc.errors.length} atlandı`,
        description: sonuc.errors[0]?.detail,
        tone: sonuc.updated > 0 ? 'success' : 'error',
      })
    }
    onBitti()
  }

  const durumDegis = useMutation({
    mutationFn: async () => {
      if (!hedefDurum) return undefined
      const { data } = await api.POST('/api/v1/members/bulk/status', {
        body: { membership_ids: kimlikler, status: hedefDurum },
      })
      return data
    },
    onSuccess: sonucuBildir,
    onError: (error) =>
      toast({
        title: 'Uygulanamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const tipDegis = useMutation({
    mutationFn: async () => {
      if (!hedefTip) return undefined
      const { data } = await api.POST('/api/v1/members/bulk/type', {
        body: { membership_ids: kimlikler, membership_type_id: hedefTip },
      })
      return data
    },
    onSuccess: sonucuBildir,
    onError: (error) =>
      toast({
        title: 'Uygulanamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const secimSinifi =
    'h-9 rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-2 text-sm'

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-control)] bg-[var(--ad-primary)]/10 px-3 py-2 text-sm">
      <strong>{kimlikler.length} üye seçili</strong>
      <span className="flex items-center gap-1">
        <select
          aria-label="Yeni durum"
          className={secimSinifi}
          value={hedefDurum}
          onChange={(event) => setHedefDurum(event.target.value as Status | '')}
        >
          <option value="">Durum…</option>
          {(['active', 'passive', 'resigned', 'expelled', 'deceased'] as Status[]).map((durum) => (
            <option key={durum} value={durum}>
              {DURUM[durum].label}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          variant="secondary"
          disabled={!hedefDurum || durumDegis.isPending}
          onClick={() => durumDegis.mutate()}
        >
          Uygula
        </Button>
      </span>
      <span className="flex items-center gap-1">
        <select
          aria-label="Yeni üyelik tipi"
          className={secimSinifi}
          value={hedefTip}
          onChange={(event) => setHedefTip(event.target.value)}
        >
          <option value="">Üyelik tipi…</option>
          {tipler.map((tip) => (
            <option key={tip.id} value={tip.id}>
              {tip.name}
            </option>
          ))}
        </select>
        <Button
          size="sm"
          variant="secondary"
          disabled={!hedefTip || tipDegis.isPending}
          onClick={() => tipDegis.mutate()}
        >
          Uygula
        </Button>
      </span>
      <Button size="sm" variant="ghost" className="ml-auto" onClick={onBitti}>
        Seçimi temizle
      </Button>
    </div>
  )
}

function YeniUye({
  acik,
  onAcik,
  tipler,
  slug,
}: {
  acik: boolean
  onAcik: (acik: boolean) => void
  tipler: MembershipType[]
  slug: string | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [hatalar, setHatalar] = useState<Record<string, string>>({})
  const ozelAlanlar = useOzelAlanlar()

  const kaydet = useMutation({
    mutationFn: async (form: FormData) => {
      const tckn = String(form.get('national_id') ?? '').trim()
      const { data } = await api.POST('/api/v1/members', {
        body: {
          first_name: String(form.get('first_name')),
          last_name: String(form.get('last_name')),
          membership_type_id: String(form.get('membership_type_id')),
          email: String(form.get('email') || '') || undefined,
          phone: String(form.get('phone') || '') || undefined,
          national_id: tckn || undefined,
          custom_fields: ozelAlanDegerleri(form, ozelAlanlar.data ?? []),
        },
      })
      return data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', slug] })
      onAcik(false)
      setHatalar({})
      toast({ title: 'Başvuru oluşturuldu', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) {
        setHatalar(error.fieldErrors())
        toast({ title: 'Kaydedilemedi', description: error.problem.detail, tone: 'error' })
        return
      }
      toast({ title: 'Kaydedilemedi', tone: 'error' })
    },
  })

  return (
    <Dialog
      open={acik}
      onOpenChange={onAcik}
      title="Üye ekle"
      description="Kayıt başvuru olarak açılır; yönetim kurulu kararıyla üyelik başlar."
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          kaydet.mutate(new FormData(event.currentTarget))
        }}
      >
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Ad" required error={hatalar.first_name}>
            {(props) => <Input name="first_name" autoFocus {...props} />}
          </Field>
          <Field label="Soyad" required error={hatalar.last_name}>
            {(props) => <Input name="last_name" {...props} />}
          </Field>
        </div>

        <Field label="Üyelik tipi" required error={hatalar.membership_type_id}>
          {(props) => (
            <select
              name="membership_type_id"
              className="h-10 w-full rounded-[var(--radius-control)] border border-[var(--ad-border)] bg-[var(--ad-surface)] px-3 text-sm"
              {...props}
            >
              {tipler.map((tip) => (
                <option key={tip.id} value={tip.id}>
                  {tip.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="E-posta" error={hatalar.email}>
            {(props) => <Input name="email" type="email" {...props} />}
          </Field>
          <Field label="Telefon" error={hatalar.phone}>
            {(props) => <Input name="phone" inputMode="tel" {...props} />}
          </Field>
        </div>

        <Field
          label="TC Kimlik No"
          error={hatalar.national_id}
          hint="Zorunlu değil. Listede maskeli görünür; açık hâli ayrı yetki ister ve okunduğu denetim kaydına düşer."
        >
          {(props) => <Input name="national_id" inputMode="numeric" maxLength={11} {...props} />}
        </Field>

        <OzelAlanlar alanlar={ozelAlanlar.data ?? []} />

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={() => onAcik(false)}>
            Vazgeç
          </Button>
          <Button type="submit" disabled={kaydet.isPending || tipler.length === 0}>
            {kaydet.isPending ? 'Kaydediliyor…' : 'Kaydet'}
          </Button>
        </div>

        {tipler.length === 0 && (
          <p className="text-xs text-[var(--ad-danger)]">
            Önce en az bir üyelik tipi tanımlanmalı.
          </p>
        )}
      </form>
    </Dialog>
  )
}

function UyeDetay({
  uye,
  onKapat,
  slug,
}: {
  uye: Member
  onKapat: () => void
  slug: string | null
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [acikTckn, setAcikTckn] = useState<string | null>(null)
  const [duzenleme, setDuzenleme] = useState(false)
  const [duzenlemeHatalari, setDuzenlemeHatalari] = useState<Record<string, string>>({})

  const tazele = () => {
    void queryClient.invalidateQueries({ queryKey: ['members', slug] })
    onKapat()
  }

  // Çekirdek alanların düzenlenmesi (YES-158). Yalnızca DEĞİŞEN alanlar
  // gönderiliyor: sunucu `exclude_unset` ile "gönderilmeyen alana dokunma"
  // kuralı uyguluyor; her şeyi göndermek, başka bir yöneticinin az önce
  // yaptığı değişikliği eski değerle ezerdi.
  const duzenle = useMutation({
    mutationFn: async (form: FormData) => {
      const oku = (ad: string) => String(form.get(ad) ?? '').trim()
      const degisen: Record<string, string | null> = {}
      const alanlar: [string, string | null][] = [
        ['first_name', uye.first_name],
        ['last_name', uye.last_name],
        ['email', uye.email],
        ['phone', uye.phone],
        ['birth_date', uye.birth_date],
      ]
      for (const [ad, eski] of alanlar) {
        const yeni = oku(ad)
        if (yeni !== (eski ?? '')) degisen[ad] = yeni === '' ? null : yeni
      }
      if (Object.keys(degisen).length === 0) return
      await api.PATCH('/api/v1/members/{membership_id}', {
        params: { path: { membership_id: uye.id } },
        body: degisen,
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', slug] })
      setDuzenleme(false)
      setDuzenlemeHatalari({})
      toast({ title: 'Üye bilgileri güncellendi', tone: 'success' })
    },
    onError: (error) => {
      if (error instanceof ApiError) setDuzenlemeHatalari(error.fieldErrors())
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      })
    },
  })

  const karar = useMutation({
    mutationFn: async (kabul: boolean) => {
      await api.POST('/api/v1/members/{membership_id}/decision', {
        params: { path: { membership_id: uye.id } },
        body: { approved: kabul, board_decision_date: bugun() },
      })
    },
    onSuccess: (_data, kabul) => {
      toast({ title: kabul ? 'Üyelik başladı' : 'Başvuru reddedildi', tone: 'success' })
      tazele()
    },
    onError: (error) =>
      toast({
        title: 'İşlem yapılamadı',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const durumDegis = useMutation({
    mutationFn: async (hedef: Status) => {
      await api.POST('/api/v1/members/{membership_id}/status', {
        params: { path: { membership_id: uye.id } },
        body: { status: hedef },
      })
    },
    onSuccess: () => {
      toast({ title: 'Üyelik durumu güncellendi', tone: 'success' })
      tazele()
    },
    onError: (error) =>
      toast({
        title: 'Durum değiştirilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  // Aidat ekstresi (YES-161). `fees.read` ister; Personel rolünde yoktur ve
  // 403 gelir — o durumda bölüm hiç çizilmez, hata gösterilmez: üye kartını
  // açan herkesin aidat görmesi gerekmiyor.
  const ekstre = useQuery({
    queryKey: ['fee-statement', slug, uye.id],
    queryFn: async () => {
      const { data } = await api.GET('/api/v1/fees/statement/{membership_id}', {
        params: { path: { membership_id: uye.id } },
      })
      return data ?? []
    },
    retry: false,
  })
  const ekstreYetkisiz = ekstre.error instanceof ApiError && ekstre.error.problem.status === 403

  const ozelAlanlar = useOzelAlanlar()
  const ozelKaydet = useMutation({
    mutationFn: async (form: FormData) => {
      await api.PATCH('/api/v1/members/{membership_id}', {
        params: { path: { membership_id: uye.id } },
        // YALNIZCA özel alanlar gönderiliyor: gönderilmeyen çekirdek alana
        // dokunulmuyor (sunucu `exclude_unset` ile ayırıyor).
        body: { custom_fields: ozelAlanDegerleri(form, ozelAlanlar.data ?? []) },
      })
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['members', slug] })
      toast({ title: 'Özel alanlar kaydedildi', tone: 'success' })
    },
    onError: (error) =>
      toast({
        title: 'Kaydedilemedi',
        description: error instanceof ApiError ? error.problem.detail : undefined,
        tone: 'error',
      }),
  })

  const tcknGoster = useMutation({
    mutationFn: async () => {
      const { data } = await api.GET('/api/v1/members/{membership_id}/national-id', {
        params: { path: { membership_id: uye.id } },
      })
      return data?.national_id ?? null
    },
    onSuccess: (deger) => setAcikTckn(deger),
    onError: (error) =>
      toast({
        title: 'Görüntülenemedi',
        description:
          error instanceof ApiError && error.problem.status === 403
            ? 'Bu bilgiyi görmek için ayrı bir yetki gerekiyor.'
            : undefined,
        tone: 'error',
      }),
  })

  return (
    <Dialog
      open
      onOpenChange={(acik) => !acik && onKapat()}
      title={`${uye.first_name} ${uye.last_name}`}
      description={uye.member_no ? `Üye no ${uye.member_no}` : 'Başvuru'}
    >
      <div className="flex flex-col gap-4 text-sm">
        {duzenleme ? (
          <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
              event.preventDefault()
              duzenle.mutate(new FormData(event.currentTarget))
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="Ad" required error={duzenlemeHatalari.first_name}>
                {(props) => (
                  <Input name="first_name" defaultValue={uye.first_name} autoFocus {...props} />
                )}
              </Field>
              <Field label="Soyad" required error={duzenlemeHatalari.last_name}>
                {(props) => <Input name="last_name" defaultValue={uye.last_name} {...props} />}
              </Field>
              <Field label="E-posta" error={duzenlemeHatalari.email}>
                {(props) => (
                  <Input name="email" type="email" defaultValue={uye.email ?? ''} {...props} />
                )}
              </Field>
              <Field label="Telefon" error={duzenlemeHatalari.phone}>
                {(props) => (
                  <Input name="phone" inputMode="tel" defaultValue={uye.phone ?? ''} {...props} />
                )}
              </Field>
              <Field label="Doğum tarihi" error={duzenlemeHatalari.birth_date}>
                {(props) => (
                  <Input
                    name="birth_date"
                    type="date"
                    defaultValue={uye.birth_date ?? ''}
                    {...props}
                  />
                )}
              </Field>
            </div>
            {/* TCKN burada düzenlenmiyor: ayrı izin ister ve her görüntüleme
                denetim kaydına düşer (ADR-14). Yanlış girilmişse kaydı silip
                yeniden açmak, sessizce değiştirmekten daha izlenebilir. */}
            <p className="text-xs text-[var(--ad-text-muted)]">
              TC kimlik numarası bu formdan değiştirilemez.
            </p>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={() => setDuzenleme(false)}>
                Vazgeç
              </Button>
              <Button type="submit" disabled={duzenle.isPending}>
                {duzenle.isPending ? 'Kaydediliyor…' : 'Kaydet'}
              </Button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <dt className="text-[var(--ad-text-muted)]">Üyelik tipi</dt>
            <dd>{uye.membership_type_name}</dd>
            <dt className="text-[var(--ad-text-muted)]">Durum</dt>
            <dd>
              <Badge tone={DURUM[uye.status].tone}>{DURUM[uye.status].label}</Badge>
            </dd>
            <dt className="text-[var(--ad-text-muted)]">E-posta</dt>
            <dd>{uye.email ?? '—'}</dd>
            <dt className="text-[var(--ad-text-muted)]">Telefon</dt>
            <dd>{uye.phone ?? '—'}</dd>
            <dt className="text-[var(--ad-text-muted)]">Doğum tarihi</dt>
            <dd>{uye.birth_date ?? '—'}</dd>
            <dt className="text-[var(--ad-text-muted)]">Başvuru</dt>
            <dd>{uye.applied_at ?? '—'}</dd>
            <dt className="text-[var(--ad-text-muted)]">Karar tarihi</dt>
            <dd>{uye.board_decision_date ?? '—'}</dd>
            <dt className="text-[var(--ad-text-muted)]" />
            <dd>
              <Button type="button" variant="ghost" size="sm" onClick={() => setDuzenleme(true)}>
                Bilgileri düzenle
              </Button>
            </dd>
          </dl>
        )}

        {uye.national_id_masked && (
          <div className="flex items-center gap-2 rounded-[var(--radius-control)] bg-[var(--ad-surface-muted)] px-3 py-2">
            <span className="text-[var(--ad-text-muted)]">TCKN</span>
            <span className="font-mono tabular-nums">{acikTckn ?? uye.national_id_masked}</span>
            {acikTckn === null && (
              <Button
                variant="ghost"
                size="sm"
                className="ml-auto"
                disabled={tcknGoster.isPending}
                onClick={() => tcknGoster.mutate()}
              >
                {/* Görüntüleme denetim kaydına düşüyor (ADR-14); düğme bunu
                    söylüyor ki kimse farkında olmadan iz bırakmasın. */}
                Görüntüle · kayda geçer
              </Button>
            )}
          </div>
        )}

        {!ekstreYetkisiz && ekstre.data && (
          <section className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-[var(--ad-text-muted)]">
                Aidat ekstresi
              </p>
              {ekstre.data.length > 0 && (
                <span className="text-xs text-[var(--ad-text-muted)]">
                  Açık borç{' '}
                  <strong className="tabular-nums text-[var(--ad-text)]">
                    {tl(
                      ekstre.data
                        .filter((satir) => ACIK_BORC.has(satir.status))
                        .reduce((toplam, satir) => toplam + satir.amount, 0),
                    )}
                  </strong>
                </span>
              )}
            </div>
            {ekstre.data.length === 0 ? (
              <p className="text-xs text-[var(--ad-text-muted)]">
                Henüz borçlandırma yok. Aidat tarifesi tanımlı ve üyelik aktifse gece koşusu ekler.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-[var(--ad-text-muted)]">
                    <th className="py-1 pr-3 font-medium">Dönem</th>
                    <th className="py-1 pr-3 font-medium">Belge</th>
                    <th className="py-1 pr-3 text-right font-medium">Tutar</th>
                    <th className="py-1 font-medium">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {ekstre.data.map((satir) => (
                    <tr key={satir.order_number} className="border-t border-[var(--ad-border)]">
                      <td className="py-1 pr-3 tabular-nums">{satir.period_key}</td>
                      <td className="py-1 pr-3 font-mono text-xs">{satir.order_number}</td>
                      <td className="py-1 pr-3 text-right tabular-nums">{tl(satir.amount)}</td>
                      <td className="py-1">
                        <Badge
                          tone={
                            satir.status === 'paid'
                              ? 'success'
                              : ACIK_BORC.has(satir.status)
                                ? 'warning'
                                : 'neutral'
                          }
                        >
                          {EKSTRE_DURUM[satir.status] ?? satir.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        )}

        {(ozelAlanlar.data ?? []).length > 0 && (
          <form
            onSubmit={(event) => {
              event.preventDefault()
              ozelKaydet.mutate(new FormData(event.currentTarget))
            }}
            className="flex flex-col gap-3"
          >
            {/* `key` ile yeniden kuruluyor: alanlar kontrolsüz, üyeden üyeye
                geçerken önceki üyenin değerleri DOM'da kalırdı. */}
            <OzelAlanlar
              key={uye.id}
              alanlar={ozelAlanlar.data ?? []}
              degerler={uye.custom_fields}
            />
            <div className="flex justify-end">
              <Button type="submit" variant="secondary" size="sm" disabled={ozelKaydet.isPending}>
                {ozelKaydet.isPending ? 'Kaydediliyor…' : 'Özel alanları kaydet'}
              </Button>
            </div>
          </form>
        )}

        <div className="flex flex-wrap justify-end gap-2 border-t border-[var(--ad-border)] pt-3">
          {uye.status === 'pending' && (
            <>
              <Button
                variant="secondary"
                disabled={karar.isPending}
                onClick={() => karar.mutate(false)}
              >
                Reddet
              </Button>
              <Button disabled={karar.isPending} onClick={() => karar.mutate(true)}>
                Üyeliği başlat
              </Button>
            </>
          )}
          {uye.status === 'active' && (
            <>
              <Button
                variant="secondary"
                disabled={durumDegis.isPending}
                onClick={() => durumDegis.mutate('passive')}
              >
                Pasife al
              </Button>
              <Button
                variant="secondary"
                disabled={durumDegis.isPending}
                onClick={() => durumDegis.mutate('resigned')}
              >
                İstifa
              </Button>
            </>
          )}
          {uye.status === 'passive' && (
            <Button disabled={durumDegis.isPending} onClick={() => durumDegis.mutate('active')}>
              Aktife al
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  )
}
